// Ticket details modal
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Paperclip, Send, X } from "lucide-react";
import { RoleEnum, TicketStatusesEnum, ContextEnum, NotificationStatusEnum, NotificationTypeEnum, NotificationPriorityEnum } from "../../shared/model";
import Spinner from "../../shared/components/Spinner";
import Button from "../../../components/ui/Button";
import { useAppDispatch, createChat, UpdateUserChats, createConversation, getUserConversations } from "../../../redux";
import { uploadContractFile } from "../../../redux/Slices/contractSlice";
import { useTranslation } from "react-i18next";
import { useWebSocket } from "../../../hooks/useWebSocket";
import { createUserNotificationAction } from "../../../redux/Actions/UserNotificationActions";
import { useSelector } from "react-redux";

interface Attachment {
    id: string;
    name: string;
    size: string;
    url?: string;
    type?: string;
}

interface Message {
    id: number;
    sender: string;
    role: string;
    message: string;
    timestamp: string;
    attachments: Attachment[];
}

const ProfessionalTicketDetailsModal = ({
    ticket,
    onClose,
    StatusBadge,
    PriorityBadge,
    getCategoryName,
    getPriorityName,
    newMessage,
    attachments,
    setAttachments,
    getRoleName,
    user,
    setNewMessage,
    onSendMessage,
    conversations,
}: any) => {
    const { t } = useTranslation();
    if (!ticket) return null;
    const [messages, setMessages] = useState<Message[]>(ticket.messages || []);
    const dispatch = useAppDispatch();
    const { chats } = useSelector((state: any) => state.auth);
    const [isUploading, setIsUploading] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    
    // WebSocket for real-time messaging
    const { isConnected, sendMessage: sendWebSocketMessage } = useWebSocket({
        onMessage: (message) => {
            console.log("====================================");
            console.log("📨 [PROFESSIONAL MODAL] WebSocket message received");
            console.log("Message Type:", message.type);
            console.log("Message Detail:", message.detail);
            console.log("Current Ticket ID:", ticket.id);
            console.log("Message Ticket ID:", message.detail?.ticketId);
            console.log("Current Chat ID:", chats?.id);
            console.log("Message Chat ID:", message.detail?.chatId);
            console.log("====================================");
            
            // Handle both message types for compatibility
            if ((message.type === "new_support_message" || message.type === "new_conversation") && 
                message.detail?.ticketId === ticket.id) {
                console.log("✅ [PROFESSIONAL MODAL] Message type and ticket ID match!");
                console.log("🔄 [PROFESSIONAL MODAL] Refreshing conversations for ticket:", ticket.id);
                console.log("🔄 [PROFESSIONAL MODAL] Using chat ID:", chats?.id);
                
                // Refresh conversations to get the latest messages
                if (chats?.id) {
                    dispatch(getUserConversations({ id: chats.id })).then(() => {
                        console.log("✅ [PROFESSIONAL MODAL] Conversations refreshed successfully");
                    }).catch((err) => {
                        console.error("❌ [PROFESSIONAL MODAL] Failed to refresh conversations:", err);
                    });
                } else {
                    console.warn("⚠️ [PROFESSIONAL MODAL] No chat ID available, cannot refresh conversations");
                }
            } else {
                console.log("❌ [PROFESSIONAL MODAL] Message does not match criteria");
                console.log("   - Is new_support_message or new_conversation?", 
                    message.type === "new_support_message" || message.type === "new_conversation");
                console.log("   - Ticket ID matches?", message.detail?.ticketId === ticket.id);
            }
        }
    });

    console.log("🔌 [PROFESSIONAL MODAL] WebSocket connected:", isConnected);
    console.log("🎫 [PROFESSIONAL MODAL] Current ticket:", ticket.id);
    console.log("💬 [PROFESSIONAL MODAL] Current chat:", chats?.id);

    const formatTimestamp = timestamp => {
        return new Date(timestamp).toLocaleString();
    };

    const handleSendWithAttachments = async () => {
        if (newMessage.trim() || attachments.length > 0) {
            try {
                console.log("====================================");
                console.log("📤 [PROFESSIONAL MODAL] Sending message...");
                console.log("Message:", newMessage);
                console.log("Attachments:", attachments.length);
                console.log("Ticket ID:", ticket.id);
                console.log("User ID:", user?.id);
                console.log("====================================");

                // Create or update chat
                const chatPayload = {
                    unreadCount: {},
                    participants: [{ id: user?.id }],
                    contextId: ticket.id,
                    contextType: { id: ContextEnum.Support_Ticket },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: newMessage || t("ticketDetailModal.sentAttachments", { count: attachments.length }),
                };

                let chatResponse;
                if (chats) {
                    console.log("🔄 [PROFESSIONAL MODAL] Updating existing chat:", chats.id);
                    chatResponse = await dispatch(UpdateUserChats({ id: chats.id, data: chatPayload })).unwrap();
                } else {
                    console.log("➕ [PROFESSIONAL MODAL] Creating new chat");
                    chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
                }
                console.log("✅ [PROFESSIONAL MODAL] Chat response:", chatResponse);

                // Create conversation with attachments
                const conversationPayload = {
                    isRead: false,
                    message: newMessage,
                    sender: { id: user?.id },
                    chatId: { id: chatResponse.id },
                    attachments: attachments.map(att => ({ id: att.id })),
                };

                console.log("💬 [PROFESSIONAL MODAL] Creating conversation...");
                const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();
                console.log("✅ [PROFESSIONAL MODAL] Conversation created:", conversation.id);

                // Immediately refresh conversations to show the new message
                console.log("🔄 [PROFESSIONAL MODAL] Refreshing conversations...");
                await dispatch(getUserConversations({ id: chatResponse.id }));
                console.log("✅ [PROFESSIONAL MODAL] Conversations refreshed");

                // Send WebSocket notification to support team (non-blocking)
                setTimeout(async () => {
                    try {
                        console.log("====================================");
                        console.log("🌐 [PROFESSIONAL MODAL] Preparing WebSocket message");
                        console.log("WebSocket connected?", isConnected);
                        console.log("sendWebSocketMessage available?", !!sendWebSocketMessage);
                        
                        if (sendWebSocketMessage && isConnected) {
                            // Notify admin users
                            const receivers = ["admin"];
                            
                            const messageData = {
                                receivers,
                                sender: user?.id || "",
                                type: "new_support_message",
                                detail: {
                                    conversationId: conversation.id,
                                    chatId: chatResponse.id,
                                    message: conversationPayload.message || t("ticketDetailModal.sentAttachments", { count: attachments.length }),
                                    senderId: user?.id,
                                    ticketId: ticket.id,
                                    attachments: conversationPayload.attachments,
                                    timestamp: new Date().toISOString()
                                }
                            };
                            
                            console.log("📡 [PROFESSIONAL MODAL] Sending WebSocket message to admins:", messageData);
                            const wsResponse = await sendWebSocketMessage(messageData);
                            console.log("✅ [PROFESSIONAL MODAL] WebSocket message sent successfully");
                            console.log("📊 [PROFESSIONAL MODAL] WebSocket Response:", wsResponse);
                            
                            if (!wsResponse.success || wsResponse.message.includes("0 user")) {
                                console.warn("⚠️ [PROFESSIONAL MODAL] No admin users connected to receive message");
                            }
                            
                            // Create notification for admin users (they will see it in their notification center)
                            console.log("🔔 [PROFESSIONAL MODAL] Notification will be created by backend notification system");
                        } else {
                            console.warn("⚠️ [PROFESSIONAL MODAL] Cannot send WebSocket message");
                            console.warn("   - isConnected:", isConnected);
                            console.warn("   - sendWebSocketMessage:", !!sendWebSocketMessage);
                        }
                        console.log("====================================");
                    } catch (wsError) {
                        console.error("❌ [PROFESSIONAL MODAL] WebSocket error:", wsError);
                    }
                }, 100);

                setNewMessage("");
                setAttachments([]);
                console.log("✅ [PROFESSIONAL MODAL] Message sent successfully");
            } catch (error) {
                console.error("❌ [PROFESSIONAL MODAL] Error sending message:", error);
            }
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);

        try {
            const newAttachments: Attachment[] = [];

            for (const file of Array.from(files)) {
                const { payload } = await dispatch(uploadContractFile(file) as any);
                const fileId = payload?.file?.id;
                const fileUrl = payload?.file?.path;

                if (fileId) {
                    newAttachments.push({
                        id: fileId,
                        name: file.name,
                        size: `${(file.size / 1024).toFixed(1)} KB`,
                        type: file.type,
                        url: fileUrl || URL.createObjectURL(file),
                    });
                }
            }

            setAttachments(prev => [...prev, ...newAttachments]);
        } finally {
            setIsUploading(false);
            setFileInputKey(Date.now());
        }
    };

    useEffect(() => {
        if (conversations && conversations.length > 0) {
            const processedMessages = conversations.map(conv => {
                const isProfessional = conv.sender?.id === user?.id;
                const senderName = isProfessional ? `${user?.firstName} ${user?.lastName}` : "Support Team";

                const mappedAttachments =
                    conv.attachments?.map(att => {
                        const extension = att.path.split(".").pop()?.toLowerCase();
                        const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");

                        return {
                            id: att.id,
                            name: att.path.split("/").pop() || "attachment",
                            size: "Unknown",
                            url: att.path,
                            type: isImage ? "image" : "file",
                        };
                    }) || [];

                return {
                    id: conv.id,
                    sender: senderName,
                    role: isProfessional ? getRoleName(RoleEnum.professional) : "support",
                    message:
                        conv.message ||
                        (mappedAttachments.length > 0 ? t("ticketDetailModal.sentAttachments", { count: mappedAttachments.length }) : ""),
                    timestamp: conv.createdAt,
                    attachments: mappedAttachments,
                };
            });

            setMessages(
                processedMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
            );
        } else {
            setMessages(ticket.messages || []);
        }
    }, [conversations, user, ticket.messages]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleRemoveAttachment = (id: string) => {
        setAttachments(prev => prev.filter(attachment => attachment.id !== id));
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 border-b">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{ticket.subject}</h2>
                            <div className="flex items-center gap-3 mt-2">
                                <p className="text-sm text-gray-600">{t("ticketDetailModal.ticketNumber", { id: ticket.id })}</p>
                                <StatusBadge statusId={ticket.status.id} />
                                <PriorityBadge priorityId={ticket.priority.id} />
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X className="h-6 w-6 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex">
                    {/* Main Chat Area */}
                    <div className="flex-1 overflow-hidden p-6 w-full flex flex-col">
                        {/* Messages container with hidden scrollbar but mouse scroll enabled */}
                        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto pb-4 scrollbar-hide">
                            <div className="space-y-6">
                                {messages.map(message => (
                                    <div
                                        key={message.id}
                                        className={`flex gap-4 ${
                                            message.role === getRoleName(RoleEnum.professional)
                                                ? "flex-row-reverse"
                                                : ""
                                        }`}>
                                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                            {message.sender[0]}
                                        </div>
                                        <div
                                            className={`flex-1 ${
                                                message.role === getRoleName(RoleEnum.professional) ? "text-right" : ""
                                            }`}>
                                            <div
                                                className={`flex items-center gap-2 mb-1 ${
                                                    message.role === getRoleName(RoleEnum.professional)
                                                        ? "justify-end"
                                                        : ""
                                                }`}>
                                                <span className="font-medium text-gray-900">{message.sender}</span>
                                                <span className="text-sm text-gray-500">
                                                    {formatTimestamp(message.timestamp)}
                                                </span>
                                            </div>
                                            <div className="inline-block p-4 rounded-lg w-full bg-gray-100 text-gray-900">
                                                <p>{message.message}</p>
                                                {message.attachments.length > 0 && (
                                                    <div className="mt-3 space-y-2">
                                                        {message.attachments.map((attachment, index) => (
                                                            <div key={index} className="flex flex-col gap-2">
                                                                {attachment.type === "image" && attachment.url && (
                                                                    <div className="max-w-xs rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                                                                        <img
                                                                            src={attachment.url}
                                                                            alt={attachment.name}
                                                                            className="w-full h-auto max-h-60 object-contain cursor-pointer"
                                                                            onClick={() =>
                                                                                window.open(attachment.url, "_blank")
                                                                            }
                                                                        />
                                                                    </div>
                                                                )}

                                                                <div
                                                                    className={`flex items-center gap-2 text-sm p-2 rounded ${
                                                                        attachment.type === "image"
                                                                            ? "bg-gray-50 mt-1"
                                                                            : "bg-white"
                                                                    }`}>
                                                                    <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                    <span className="truncate">{attachment.name}</span>
                                                                    <span className="text-gray-400 text-xs whitespace-nowrap">
                                                                        ({attachment.size})
                                                                    </span>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="ml-auto p-1"
                                                                        onClick={e => {
                                                                            e.stopPropagation();
                                                                            window.open(attachment.url, "_blank");
                                                                        }}>
                                                                        <ExternalLink className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        {/* Attachment Preview */}
                        {attachments.length > 0 && (
                            <div className="mb-4 space-y-2">
                                {attachments.map(attachment => (
                                    <div
                                        key={attachment.id}
                                        className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                                        <Paperclip className="w-4 h-4 text-gray-400" />
                                        <span className="flex-1">{attachment.name}</span>
                                        <span className="text-gray-400">({attachment.size})</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveAttachment(attachment.id)}>
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Input section */}
                        <div className="flex gap-4 mt-4">
                            <textarea
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                placeholder={t("ticketDetailModal.messagePlaceholder")}
                                className="flex-1 p-3 border rounded-lg resize-none"
                                rows={3}
                            />
                            <div className="space-y-2">
                                <Button
                                    className="w-full"
                                    disabled={!newMessage.trim() && attachments.length === 0}
                                    onClick={handleSendWithAttachments}>
                                    <Send className="w-4 h-4 mr-2" />
                                    {t("ticketDetailModal.send")}
                                </Button>
                                <div className="relative">
                                    <Button variant="outline" className="w-full">
                                        {isUploading ? (
                                            <>
                                                <Spinner size={4} />
                                                {t("ticketDetailModal.uploading")}
                                            </>
                                        ) : (
                                            <>
                                                <Paperclip className="w-4 h-4 mr-2" />
                                                {t("ticketDetailModal.attach")}
                                            </>
                                        )}
                                    </Button>
                                    <input
                                        key={fileInputKey}
                                        type="file"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        multiple
                                        onChange={handleFileUpload}
                                        disabled={isUploading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="w-80 border-l bg-gray-50 overflow-y-auto">
                        <div className="p-6 space-y-6">
                            {/* Ticket Details */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3">{t("ticketDetailModal.ticketDetails")}</h3>
                                <dl className="space-y-2">
                                    <div>
                                        <dt className="text-sm text-gray-500">{t("ticketDetailModal.created")}</dt>
                                        <dd className="text-sm text-gray-900">
                                            {new Date(ticket.createdAt).toLocaleDateString()}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm text-gray-500">{t("ticketDetailModal.category")}</dt>
                                        <dd className="text-sm text-gray-900">{getCategoryName(ticket.category.id)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm text-gray-500">{t("ticketDetailModal.priority")}</dt>
                                        <dd className="text-sm text-gray-900">{getPriorityName(ticket.priority.id)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm text-gray-500">{t("ticketDetailModal.assignedTo")}</dt>
                                        <dd className="text-sm text-gray-900">{ticket.assignedTo}</dd>
                                    </div>
                                </dl>
                            </div>

                            {/* User Details */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3">{t("ticketDetailModal.userDetails")}</h3>
                                <dl className="space-y-2">
                                    <div>
                                        <dt className="text-sm text-gray-500">{t("ticketDetailModal.name")}</dt>
                                        <dd className="text-sm text-gray-900">
                                            {ticket.userId.firstName} {ticket.userId.lastName}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm text-gray-500">{t("ticketDetailModal.email")}</dt>
                                        <dd className="text-sm text-gray-900">{ticket.userId.email}</dd>
                                    </div>
                                </dl>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3">{t("ticketDetailModal.tags")}</h3>
                                <div className="flex flex-wrap gap-2">
                                    {ticket?.tags?.length > 0 &&
                                        ticket.tags.map(tag => (
                                            <span
                                                key={tag}
                                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                                {tag}
                                            </span>
                                        ))}
                                </div>
                            </div>

                            {/* Actions */}
                            {ticket.status.id !== TicketStatusesEnum.Resolved && (
                                <div className="space-y-2">
                                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                        {ticket.status.id === TicketStatusesEnum.Resolved
                                            ? t("ticketDetailModal.reopenTicket")
                                            : t("ticketDetailModal.markAsResolved")}
                                    </button>
                                    <button className="w-full px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50">
                                        {t("ticketDetailModal.closeTicket")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ProfessionalTicketDetailsModal;
